const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
const TIME_ONLY_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/;
const AM_PM_TIME_PATTERN = /^(\d{1,2})(?::(\d{2}))?\s*([AP]M)$/i;
const LOCAL_DATETIME_TIME_PATTERN = /T(\d{1,2}):(\d{2})(?::(\d{2}))?/;
const TIMEZONE_SUFFIX_PATTERN = /(?:z|[+-]\d{2}:?\d{2})$/i;

const normalizeString = value =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const pad2 = value => String(value).padStart(2, '0');

const firstPresent = values =>
  values.find(value => value !== null && value !== undefined && String(value).trim() !== '');

export const getDateOnlyFromValue = value => {
  if (!value) {
    return null;
  }

  if (value?.localDate) {
    return getDateOnlyFromValue(value.localDate);
  }

  if (value?.fullDate) {
    return getDateOnlyFromValue(value.fullDate);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }

  const stringValue = String(value).trim();
  const dateOnlyMatch = stringValue.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
  }

  const parsed = new Date(stringValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
};

export const getLocalDateParts = value => {
  const dateOnly = getDateOnlyFromValue(value);
  const dateOnlyMatch = dateOnly?.match(DATE_ONLY_PATTERN);

  if (!dateOnlyMatch) {
    return null;
  }

  const year = Number(dateOnlyMatch[1]);
  const month = Number(dateOnlyMatch[2]);
  const day = Number(dateOnlyMatch[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return {
    dateOnly,
    year: String(year),
    month: String(month),
    monthShort: date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }),
    monthLong: date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' }),
    day: String(day),
    weekday: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
  };
};

export const normalizeLocalTime = value => {
  if (!value) {
    return null;
  }

  const stringValue = String(value).trim();
  if (!stringValue) {
    return null;
  }

  const amPmMatch = stringValue.match(AM_PM_TIME_PATTERN);
  if (amPmMatch) {
    let hour = Number(amPmMatch[1]);
    const minute = Number(amPmMatch[2] || '0');
    const period = amPmMatch[3].toUpperCase();

    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) {
      return null;
    }

    if (period === 'PM' && hour < 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }

    return `${pad2(hour)}:${pad2(minute)}:00`;
  }

  const timeOnlyMatch = stringValue.match(TIME_ONLY_PATTERN);
  if (timeOnlyMatch) {
    const hour = Number(timeOnlyMatch[1]);
    const minute = Number(timeOnlyMatch[2]);
    const second = Number(timeOnlyMatch[3] || '0');

    if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) {
      return null;
    }

    return `${pad2(hour)}:${pad2(minute)}:${pad2(second)}`;
  }

  if (!TIMEZONE_SUFFIX_PATTERN.test(stringValue)) {
    const localDateTimeMatch = stringValue.match(LOCAL_DATETIME_TIME_PATTERN);
    if (localDateTimeMatch) {
      return normalizeLocalTime(
        `${localDateTimeMatch[1]}:${localDateTimeMatch[2]}:${localDateTimeMatch[3] || '00'}`
      );
    }
  }

  return null;
};

export const formatLocalTimeForDisplay = value => {
  const normalizedTime = normalizeLocalTime(value);
  if (!normalizedTime) {
    return null;
  }

  const [hourRaw, minuteRaw] = normalizedTime.split(':');
  const hour = Number(hourRaw);
  const minute = pad2(Number(minuteRaw));
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

export const formatLocalDateForDisplay = (value, options = {}) => {
  const { includeYear = true, month = 'short' } = options;
  const parts = getLocalDateParts(value);

  if (!parts) {
    return '';
  }

  const year = Number(parts.year);
  const monthIndex = Number(parts.month) - 1;
  const day = Number(parts.day);
  const date = new Date(Date.UTC(year, monthIndex, day));

  return date.toLocaleDateString('en-US', {
    month,
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
};

export const formatLocalDateTimeForDisplay = ({
  localDate,
  localTime,
  fallbackDateTime = null,
  includeYear = true,
} = {}) => {
  const dateLabel = formatLocalDateForDisplay(localDate || fallbackDateTime, { includeYear });
  const fallbackLocalDateTime =
    !localTime && fallbackDateTime && !TIMEZONE_SUFFIX_PATTERN.test(String(fallbackDateTime))
      ? fallbackDateTime
      : null;
  const timeLabel = formatLocalTimeForDisplay(localTime || fallbackLocalDateTime);

  return [dateLabel, timeLabel].filter(Boolean).join(', ');
};

export const buildLocalDateTimeValue = (localDate, localTime) => {
  const dateOnly = getDateOnlyFromValue(localDate);

  if (!dateOnly) {
    return null;
  }

  const normalizedTime = normalizeLocalTime(localTime);
  return normalizedTime ? `${dateOnly}T${normalizedTime}` : dateOnly;
};

export const getTicketmasterLocalParts = (...sources) => {
  const sourceList = sources.filter(Boolean);
  const nestedSources = sourceList
    .flatMap(source => [source, source?.details, source?.attributes?.publicData])
    .filter(Boolean);

  const localDate = firstPresent(
    nestedSources.flatMap(source => [
      source?.dates?.start?.localDate,
      source?.eventDate?.localDate,
      source?.localDate,
      source?.event_date,
      source?.date,
    ])
  );
  const localTime = firstPresent(
    nestedSources.flatMap(source => [
      source?.dates?.start?.localTime,
      source?.eventDate?.localTime,
      source?.localTime,
      source?.event_time,
      source?.eventTime,
      source?.time,
    ])
  );
  const dateTime = firstPresent(
    nestedSources.flatMap(source => [
      source?.dates?.start?.dateTime,
      source?.dateTime,
      source?.eventDate?.dateTime,
      source?.eventDate?.fullDate,
      source?.eventDateTime,
      typeof source?.dates === 'string' ? source.dates : null,
    ])
  );
  const timezone = firstPresent(
    nestedSources.flatMap(source => [source?.dates?.timezone, source?.timezone])
  );

  return {
    localDate: getDateOnlyFromValue(localDate) || getDateOnlyFromValue(dateTime),
    localTime: normalizeLocalTime(localTime),
    dateTime: normalizeString(dateTime),
    timezone: normalizeString(timezone),
  };
};
